// Lion Auto scheduled auto-run, as the page shows it: the runner's status in
// plain words, the dry-run / live mode, the cadence table, the presale
// schedule (its rule, a group's chip, the Lion Checker sync), when a group is
// next due, the chip on a group card (keyed off the group's autoLastStatus),
// the badge on a Dashboard event header and a run's summary ("3 repriced ·
// 2 unchanged · 1 floored · skipped 2 (…)"). Pure; tested in
// lionAutoRunner.test.js. Instants come from the
// server in UTC (SQLite "YYYY-MM-DD HH:MM:SS" or ISO) and go through
// parseUtcDate; anything absolute is shown in Eastern time.
import { parseUtcDate, timeAgo, formatDateTimeEastern, formatDateEastern } from './dates';
import { outliersDroppedText } from './lionAutoOutliers';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

// A table's own entry (never Object.prototype's: "toString" is no status)
const own = (table, key) => (typeof key === 'string' && Object.prototype.hasOwnProperty.call(table, key) ? table[key] : null);

// ---- the runner ----

// GET /auto/status → status: what each one means for a person reading it.
// kind: ok (green) | warn (amber) | bad (red) | off (grey)
const RUNNER_STATUS = Object.freeze({
  stopped: { kind: 'off', label: 'Stopped', text: 'The runner is not running on this server: nothing will run.' },
  disabled: { kind: 'off', label: 'Off', text: 'The master switch is off: nothing will run, whatever groups are on Auto.' },
  idle: { kind: 'ok', label: 'Idle', text: 'Running: waiting for the next group to fall due.' },
  working: { kind: 'ok', label: 'Working', text: 'Repricing a group right now.' },
  paused_auth: {
    kind: 'bad',
    label: 'Login(s) down',
    text: "Paused: Skybox refused every Auto login's session. Nothing runs until one is refreshed; the runner keeps checking.",
  },
  no_session: { kind: 'bad', label: 'No login', text: 'No Auto login assigned: nothing will run.' },
  paused_room: {
    kind: 'warn',
    label: 'Waiting for room',
    text: "Holding back: pricers are using the Skybox pricing budget right now. It runs when there's room.",
  },
});

/** The runner status as { kind, label, text }; an unknown one is shown as sent */
export function runnerStatusInfo(status) {
  const known = own(RUNNER_STATUS, status);
  if (known) return { ...known };
  if (!status) return { kind: 'off', label: 'Unknown', text: 'The server has not said what the runner is doing.' };
  return { kind: 'off', label: String(status), text: `The runner reports “${status}”.` };
}

/**
 * Dry run unless the server says plainly it is off (the backend ships in dry
 * run): the top-level dryRun, else config.dryRun
 */
export function isDryRun(autoStatus) {
  const v = autoStatus?.dryRun ?? autoStatus?.config?.dryRun;
  return v !== false;
}

/** The master switch: the top-level enabled, else config.enabled */
export function isEnabled(autoStatus) {
  return (autoStatus?.enabled ?? autoStatus?.config?.enabled) === true;
}

/** The mode banner: dry run vs live */
export function modeBanner(dryRun) {
  return dryRun
    ? { live: false, text: 'DRY RUN: nothing is written', title: 'Auto computes each run and records what it would write; no price goes to Skybox.' }
    : { live: true, text: 'LIVE: Auto writes prices to Skybox', title: 'Each run writes its new prices to Skybox with no one reviewing them (never under the floor).' };
}

// Where a super admin assigns (or renews) Lion Auto's Skybox logins: Admin →
// Data (Admin.js opens ?tab=data directly)
export const ADMIN_DATA_PATH = '/admin?tab=data';

/**
 * The line pointing to Admin → Data when the runner can't run for want of
 * a working login (the caller appends the "Admin → Data" link), or null
 */
export function autoLoginHint(status) {
  if (status === 'no_session') return 'No Auto login assigned, so Auto does nothing. A super admin assigns a dedicated Skybox login in';
  if (status === 'paused_auth') return "Skybox refused every Auto login's session. A super admin renews it/them in";
  return null;
}

// One signed-in login in the several-logins line: its pace, a refused one says so
function loginWord(s) {
  const name = s.label ? String(s.label) : 'a login';
  if (s.authPaused) return `${name} refused`;
  if (s.pace === 'fast') return `${name} fast`;
  if (s.pace !== 'measured') return name;
  return parseUtcDate(s.last429At) ? `${name} measured (429 ${agoText(s.last429At)})` : `${name} measured`;
}

/**
 * The Auto login(s): { kind, text }. One login (or a server without
 * status.sessions): from status.session { configured, label, live }.
 * Several (status.sessions): "Auto logins: 2 of 3 signed in · lion-auto fast,
 * lion-auto-2 measured (429 3m ago)", each signed-in login with its pace (a
 * refused one says so); bad when none is usable.
 */
export function sessionText(session, sessions) {
  const list = Array.isArray(sessions) ? sessions.filter(s => s && typeof s === 'object') : [];
  if (list.length > 1) {
    const live = list.filter(s => s.live === true);
    const words = live.map(loginWord);
    return {
      kind: live.some(s => !s.authPaused) ? 'ok' : 'bad',
      text: `Auto logins: ${live.length} of ${list.length} signed in${words.length ? ` · ${words.join(', ')}` : ''}`,
    };
  }
  if (!session || !session.configured) return { kind: 'bad', text: 'No Auto login assigned' };
  const who = session.label ? String(session.label) : 'assigned';
  return session.live
    ? { kind: 'ok', text: `Auto login: ${who} · signed in` }
    : { kind: 'bad', text: `Auto login: ${who} · not signed in` };
}

/**
 * The Admin → Data card's line when GET /auto/status failed: the server's
 * error text, else the HTTP status, else the transport's message
 */
export function runnerStatusReadError(err) {
  const serverText = err?.response?.data?.error;
  const status = err?.response?.status;
  const why = (typeof serverText === 'string' && serverText)
    || (status ? `HTTP ${status}` : '')
    || (typeof err?.message === 'string' && err.message)
    || 'no answer';
  return `Couldn't read the runner's status: ${why}; logins it set aside are not shown here`;
}

/**
 * An Auto login's 429 / pace chip (Admin → Data): "429 3m ago · measured",
 * "no 429 since restart · fast"; kind warn (measured), ok (fast), off (the
 * runner did not say). last429At: the session's last 429 (UTC); pace: the
 * runner's status.sessions[].pace
 */
export function loginPaceChip(login) {
  const s = login && typeof login === 'object' ? login : {};
  const ago = parseUtcDate(s.last429At) ? `429 ${agoText(s.last429At)}` : 'no 429 since restart';
  if (s.pace === 'fast' || s.pace === 'measured') return { kind: s.pace === 'fast' ? 'ok' : 'warn', text: `${ago} · ${s.pace}` };
  return { kind: 'off', text: ago };
}

// ---- the optional per-run limits ----

// A limit as the server sent it (status.config.maxChangePct / maxWrites):
// null = no limit (the default, so a run takes each price straight to its
// ladder price, never under the floor); a number >= 0 an admin set; anything
// else (not sent, junk) is unknown (NaN). A 0 is a real 0, never "no limit".
function limitOf(v) {
  if (v === null) return null;
  if (typeof v === 'number') return Number.isFinite(v) && v >= 0 ? v : NaN;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : NaN;
  }
  return NaN;
}

/** The largest move one run makes to a price: "no limit" (null), "25%", "0% (only a floor lift)", "?" */
export function maxChangeText(v) {
  const n = limitOf(v);
  if (n === null) return 'no limit';
  if (Number.isNaN(n)) return '?';
  return n === 0 ? '0% (only a floor lift moves a price)' : `${n}%`;
}

/** The listings one run writes at most: "no limit" (null), "20 listings", "1 listing", "0 (writes nothing)", "?" */
export function maxWritesText(v) {
  const n = limitOf(v);
  if (n === null) return 'no limit';
  if (Number.isNaN(n) || !Number.isInteger(n)) return '?';
  if (n === 0) return '0 (writes nothing)';
  return `${n} listing${n === 1 ? '' : 's'}`;
}

// The panel's hover text for each limit
export function maxChangeTitle(v) {
  return limitOf(v) === null
    ? 'Each run takes a price straight to its ladder price, however far that is (never under the floor).'
    : 'Each run moves a price at most this share of its current price, toward the ladder price; a floor lift may go further.';
}

export function maxWritesTitle(v) {
  return limitOf(v) === null
    ? 'Each run writes every listing whose ladder price changed (a group ranks at most 500).'
    : 'Each run writes at most this many listings, the biggest moves first; the rest wait for the next run.';
}

// ---- confirms ----

export const DRY_RUN_OFF_CONFIRM = 'Turn dry run off? Auto will write prices to Skybox with no one watching';
export const ENABLE_LIVE_CONFIRM = 'Turn Auto on? Dry run is off: Auto will write prices to Skybox with no one watching.';

/**
 * The confirm() a config change needs, or null. change: the PUT body
 * ({ enabled } / { dryRun }); current: { enabled, dryRun } now.
 * Turning dry run off always asks; turning the master switch on asks when
 * dry run is (or is being turned) off.
 */
export function configChangeConfirm(change = {}, current = {}) {
  const dryRunAfter = change.dryRun !== undefined ? change.dryRun !== false : current.dryRun !== false;
  const enabledAfter = change.enabled !== undefined ? change.enabled === true : current.enabled === true;
  if (change.dryRun === false && current.dryRun !== false) {
    return `${DRY_RUN_OFF_CONFIRM}${enabledAfter ? '.' : ' as soon as the master switch is on.'}`;
  }
  if (change.enabled === true && current.enabled !== true && !dryRunAfter) return ENABLE_LIVE_CONFIRM;
  return null;
}

// ---- cadence ----

// The server's defaults (backend/services/lionAuto/autoCadence.js
// DEFAULT_CADENCE): shown only in words when the server sends none
export const DEFAULT_CADENCE_MS = Object.freeze({
  over6wMs: 24 * HOUR_MS,
  within6wMs: 16 * HOUR_MS,
  within4wMs: 8 * HOUR_MS,
  within2wMs: 6 * HOUR_MS,
  within7dMs: 4 * HOUR_MS,
  within3dMs: 2 * HOUR_MS,
  within24hMs: HOUR_MS,
  within3hMs: 15 * MINUTE_MS,
  startedMs: 5 * MINUTE_MS,
});

// The tiers, farthest first, timed to the show's start in the venue's time
// zone; more than 2 h after the start nothing runs. label: the table's row;
// when: the same span in cadenceSentence
export const CADENCE_TIERS = Object.freeze([
  { key: 'over6wMs', label: '6 weeks or more', when: 'when the show is 6 weeks or more away' },
  { key: 'within6wMs', label: '4 to 6 weeks', when: 'at 4 to 6 weeks' },
  { key: 'within4wMs', label: '2 to 4 weeks', when: 'at 2 to 4 weeks' },
  { key: 'within2wMs', label: '7 to 14 days', when: 'at 7 to 14 days' },
  { key: 'within7dMs', label: '3 to 7 days', when: 'at 3 to 7 days' },
  { key: 'within3dMs', label: '24 h to 3 days', when: 'from 24 h to 3 days' },
  { key: 'within24hMs', label: 'Under 24 h', when: 'under 24 h' },
  { key: 'within3hMs', label: 'Under 3 h', when: 'under 3 h' },
  { key: 'startedMs', label: 'First 2 h after the start', when: 'for the first 2 h after the start' },
]);

/** An interval in ms: "24 h", "6 h", "1 h", "15 min", "1 h 30 min"; "—" for junk */
export function formatInterval(ms) {
  const n = Number(ms);
  if (ms === null || ms === undefined || ms === '' || !Number.isFinite(n) || n <= 0) return '—';
  if (n < MINUTE_MS) return `${Math.max(1, Math.round(n / 1000))} s`;
  const totalMin = Math.round(n / MINUTE_MS);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/**
 * The cadence table: one row per tier with its interval as the server set it
 * ("—" when the server sent none for it), farthest first
 */
export function cadenceRows(cadence) {
  return CADENCE_TIERS.map(t => ({ key: t.key, label: t.label, every: formatInterval(cadence?.[t.key]) }));
}

/**
 * The cadence in a sentence for the editor's warning, one part per tier of
 * CADENCE_TIERS in order, from the server's cadence (any key missing or junk
 * takes the default; 24 h reads "daily"): "daily when the show is 6 weeks or
 * more away, every 16 h at 4 to 6 weeks, … every 15 min under 3 h and every
 * 5 min for the first 2 h after the start"
 */
export function cadenceSentence(cadence) {
  const parts = CADENCE_TIERS.map(({ key, when }) => {
    const v = Number(cadence?.[key]);
    const ms = Number.isFinite(v) && v > 0 ? v : DEFAULT_CADENCE_MS[key];
    return `${ms === 24 * HOUR_MS ? 'daily' : `every ${formatInterval(ms)}`} ${when}`;
  });
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

// ---- the presale schedule ----

// The server's presale schedule (backend/services/lionAuto/presaleCadence.js
// PRESALE_DAY_MS, PRESALE_NIGHT_MS, AFTER_ONSALE_MS): wording only, so the
// rule says what formatInterval says (lionAutoRunner.test.js pins them).
// On a show Ticketmaster lists with a presale (Lion Checker's dates) it
// replaces the tiers for that window, by the venue's clock (unknown: Eastern).
export const PRESALE_DAY_MS = 90 * MINUTE_MS;
export const PRESALE_NIGHT_MS = 3 * HOUR_MS;
export const PRESALE_AFTER_ONSALE_MS = 72 * HOUR_MS;

// The note under the panel's cadence table
export const PRESALE_RULE_TEXT = `From the first presale until ${formatInterval(PRESALE_AFTER_ONSALE_MS)} after the public onsale `
  + `(Lion Checker): every ${formatInterval(PRESALE_DAY_MS)} from 9 am to midnight, `
  + `every ${formatInterval(PRESALE_NIGHT_MS)} from midnight to 9 am, venue time.`;

// The editor warning's sentence on it
const PRESALE_WARNING = 'On a show Lion Checker lists with a Ticketmaster presale, from the first presale until '
  + `${formatInterval(PRESALE_AFTER_ONSALE_MS)} after the public onsale it re-runs every ${formatInterval(PRESALE_DAY_MS)} `
  + `from 9 am to midnight and every ${formatInterval(PRESALE_NIGHT_MS)} overnight, venue time.`;

/**
 * The chip beside a group's next run while it is on the presale schedule
 * (status.groups[].presale { everyMs, until, timeZone, zoneKnown, night }),
 * or null (none, or junk): "presale until Oct 24" (the window's end, its
 * Eastern day). Title: "Presale schedule: every 1 h 30 min until Oct 24,
 * 2026, 1:00 PM ET", "every 3 h (night, venue time)" at night, " · venue
 * zone unknown, Eastern time used" when the server fell back to Eastern.
 * @returns {{ text: string, title: string }|null}
 */
export function presaleChip(presale) {
  if (!presale || typeof presale !== 'object' || Array.isArray(presale)) return null;
  const { everyMs, until, night, zoneKnown } = presale;
  if (typeof everyMs !== 'number' || !Number.isFinite(everyMs) || everyMs <= 0) return null;
  const end = easternText(until);
  if (!end) return null;
  const every = `every ${formatInterval(everyMs)}${night === true ? ' (night, venue time)' : ''}`;
  const zone = zoneKnown === false ? ' · venue zone unknown, Eastern time used' : '';
  return {
    text: `presale until ${formatDateEastern(until, { year: undefined })}`,
    title: `Presale schedule: ${every} until ${end}${zone}`,
  };
}

/**
 * The panel's "Presale dates" line from status.presaleSync: "" when the
 * server sent none (an older server) or junk; "not set up" (no Lion Checker
 * database); "never synced"; "synced 12m ago · 7 events with sale dates"
 * (events: Auto events with Lion Checker dates stored), "· 2 unresolved"
 * when Skybox could not give some Auto events' Vivid link. A failed sync
 * leads with the server's error as sent, then the last good sync (the
 * server keeps its time and counts): "presale sync failed · last good sync
 * 3h ago · 7 events with sale dates", "… · no good sync yet" with none.
 */
export function presaleSyncText(sync) {
  if (!sync || typeof sync !== 'object' || Array.isArray(sync)) return '';
  if (sync.configured === false) return 'not set up';
  if (sync.configured !== true) return '';
  const error = typeof sync.lastError === 'string' ? sync.lastError.trim() : '';
  if (!parseUtcDate(sync.lastSyncAt)) return error ? `${error} · no good sync yet` : 'never synced';
  const unresolved = count(sync.unresolved);
  return [
    error || null,
    `${error ? 'last good sync' : 'synced'} ${agoText(sync.lastSyncAt)}`,
    `${plural(count(sync.events), 'event')} with sale dates`,
    unresolved ? `${unresolved} unresolved` : null,
  ].filter(Boolean).join(' · ');
}

/**
 * The editor's warning on a group on Auto, as the runner really behaves,
 * the presale schedule right after the cadence.
 * dryRun: the runner's mode when known (admins read it), else null.
 * saleRefill: this event's effective Reprice after a sale (GET sale-refill
 * effective.on), null while unknown; only true adds the re-run after a sale.
 */
export function autoWarningText({ cadence = null, dryRun = null, saleRefill = null } = {}) {
  let mode = 'The runner stays in dry run (it records what it would write and writes nothing) until an admin turns it live.';
  if (dryRun === true) mode = 'The runner is in dry run now: it records what it would write and writes nothing until an admin turns it live.';
  else if (dryRun === false) mode = 'The runner is LIVE now: its runs write to Skybox.';
  const afterSale = saleRefill === true
    ? ' and within a few minutes after one of its ranked listings sells (so the ranks below move up)'
    : '';
  return `Auto reprices this group with nobody reviewing: the server re-runs its ladder ${cadenceSentence(cadence)} `
    + `(never after that)${afterSale}, and writes the new prices to Skybox. ${PRESALE_WARNING} It never prices a listing under `
    + `the floor, and it leaves a listing another group holds to that group (a listing is in one group only). ${mode}`;
}

// ---- times ----

// "12m" / "3h" / "2d", the way timeAgo counts
function spanText(ms) {
  const m = Math.round(ms / MINUTE_MS);
  if (m < 60) return `${Math.max(1, m)}m`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

/**
 * When a group is next due: "due now" (at or past it), "in 12m", "in 3h",
 * "in 2d"; "not scheduled" when the server gives no time (more than 2 h
 * after the show's start)
 */
export function dueText(nextDueAt, now = Date.now()) {
  const d = parseUtcDate(nextDueAt);
  if (!d) return 'not scheduled';
  const left = d.getTime() - (parseUtcDate(now)?.getTime() ?? Date.now());
  if (left < 45 * 1000) return 'due now';
  return `in ${spanText(left)}`;
}

/** "12m ago" / "never": an instant's age (the shared timeAgo) */
export function agoText(value) {
  const d = parseUtcDate(value);
  return d ? timeAgo(d) : 'never';
}

/** "Oct 13, 2026, 11:08 AM ET", or null */
export function easternText(value) {
  return parseUtcDate(value) ? `${formatDateTimeEastern(value)} ET` : null;
}

/**
 * The soonest nextDueAt among groups (ignoring ones with none), or null
 */
export function soonestDue(groups) {
  let best = null;
  for (const g of Array.isArray(groups) ? groups : []) {
    const d = parseUtcDate(g?.nextDueAt);
    if (d && (!best || d < best)) best = d;
  }
  return best;
}

const sameInstant = (a, b) => {
  const x = parseUtcDate(a);
  const y = parseUtcDate(b);
  return (!x && !y) || (!!x && !!y && x.getTime() === y.getTime());
};

/**
 * Whether the runner's status shows this event's groups moved on from the
 * cached copy (a run since, Auto turned on elsewhere, or a group the runner
 * paused, gone from its list), so the groups are worth re-reading. Instants
 * compare as instants (the two endpoints may format them differently).
 * cachedGroups: the event's groups as cached; statusGroups: status.groups
 * (every event's groups on Auto).
 */
export function autoGroupsStale(cachedGroups, statusGroups, eventId) {
  if (!Array.isArray(cachedGroups) || !Array.isArray(statusGroups)) return false;
  const byId = new Map(cachedGroups.map(g => [String(g.id), g]));
  const listed = new Set();
  for (const sg of statusGroups) {
    if (!sg || String(sg.eventId) !== String(eventId)) continue;
    listed.add(String(sg.id));
    const g = byId.get(String(sg.id));
    if (!g) return true;
    if (!sameInstant(g.autoLastRunAt, sg.autoLastRunAt)) return true;
    if ((g.autoLastStatus ?? null) !== (sg.autoLastStatus ?? null)) return true;
    if (!sameInstant(g.autoEnabledAt, sg.autoEnabledAt)) return true;
  }
  return cachedGroups.some(g => g.autoEnabled && !listed.has(String(g.id)));
}

// ---- a group's last run ----

// The runner's autoLastStatus values, in words
const LAST_STATUS = Object.freeze({
  ok: { kind: 'ok', word: 'ok' },
  partial: { kind: 'warn', word: 'partial' },
  dry_run: { kind: 'neutral', word: 'dry run' },
  error: { kind: 'bad', word: 'error' },
  paused: { kind: 'bad', word: 'paused' },
  skipped: { kind: 'neutral', word: 'skipped' },
});

/** autoLastStatus → { kind, word }; null / unknown is ok-kind, shown as sent */
export function lastStatusInfo(status) {
  const known = own(LAST_STATUS, status);
  if (known) return { ...known };
  return { kind: 'ok', word: status ? String(status).replace(/_/g, ' ') : '' };
}

// Why the runner paused a group (autoLastSummary.reason)
const PAUSE_REASONS = Object.freeze({
  FAILURES: 'it failed several runs in a row',
  AUTH_DEAD: "Skybox refused an Auto login's session",
});

/** Why a paused group stopped, in words ("" when the server said nothing) */
export function pauseReasonText(summary) {
  const s = summary && typeof summary === 'object' ? summary : {};
  const reason = s.reason ? (own(PAUSE_REASONS, s.reason) || String(s.reason).toLowerCase().replace(/_/g, ' ')) : '';
  const error = errorText(s.error);
  if (reason && error && error !== s.reason) return `${reason}: ${error}`;
  return reason || error;
}

/**
 * Whether the group's last-run fields are from before Auto was last turned
 * on (a group paused, then fixed and switched on again, keeps its old
 * 'paused' until the runner next runs it)
 */
export function lastRunBeforeEnable(g) {
  const on = parseUtcDate(g?.autoEnabledAt);
  if (!g?.autoEnabled || !on) return false;
  const ran = parseUtcDate(g?.autoLastRunAt);
  return !ran || ran < on;
}

/**
 * The chip on a group card, or null for none:
 * - a group on Auto: "Auto · 12m ago", with its last status ("Auto · dry run
 *   · 12m ago"; ok adds no word), kind from autoLastStatus exactly
 *   (error / paused red, partial amber, dry_run / skipped neutral, ok green);
 *   "Auto · not run yet" (green) when it has not run since it was turned on
 * - a group the runner paused (Auto off, autoLastStatus 'paused'): a red
 *   "Paused" chip, its reason in the title, so someone fixes it and turns
 *   Auto back on
 * - any other group with Auto off: no chip
 * @returns {{ kind: 'ok'|'warn'|'bad'|'neutral', text: string, title: string }|null}
 */
export function autoChip(g) {
  if (!g) return null;
  if (!g.autoEnabled) {
    if (g.autoLastStatus !== 'paused') return null;
    const why = pauseReasonText(g.autoLastSummary);
    return {
      kind: 'bad',
      text: 'Paused',
      title: [
        `Auto paused${why ? `: ${why}` : ''}.`,
        g.autoLastRunAt ? `Last run ${easternText(g.autoLastRunAt)}.` : null,
        'Fix the cause, then turn Auto back on in the editor (an admin).',
      ].filter(Boolean).join('\n'),
    };
  }
  const fresh = lastRunBeforeEnable(g);
  if (fresh || !parseUtcDate(g.autoLastRunAt)) {
    return { kind: 'ok', text: 'Auto · not run yet', title: autoTitle(g) };
  }
  const info = lastStatusInfo(g.autoLastStatus);
  const word = g.autoLastStatus && g.autoLastStatus !== 'ok' ? ` · ${info.word}` : '';
  return { kind: info.kind, text: `Auto${word} · ${agoText(g.autoLastRunAt)}`, title: autoTitle(g) };
}

/** "last run 12m ago · dry run" / "not run yet" (the editor's state line) */
export function autoRunText(g) {
  if (!g || lastRunBeforeEnable(g) || !parseUtcDate(g.autoLastRunAt)) return 'not run yet';
  const { word } = lastStatusInfo(g.autoLastStatus);
  return `last run ${agoText(g.autoLastRunAt)}${word ? ` · ${word}` : ''}`;
}

/** The chip's title: who turned Auto on and when, the last run and its counts */
export function autoTitle(g) {
  const by = g.autoEnabledByName ? ` by ${g.autoEnabledByName}` : '';
  const since = easternText(g.autoEnabledAt);
  const ranAt = easternText(g.autoLastRunAt);
  const { word } = lastStatusInfo(g.autoLastStatus);
  let last = 'Not run yet.';
  if (ranAt) last = `${lastRunBeforeEnable(g) ? 'Last run (before Auto was turned on again)' : 'Last run'} ${ranAt}${word ? ` (${word})` : ''}.`;
  const summary = summaryText(g.autoLastSummary, g.autoLastStatus);
  return [
    'Auto: the server re-ladders this group on a schedule and writes its prices to Skybox unattended.',
    `On${by}${since ? ` since ${since}` : ''}.`,
    last,
    summary || null,
    g.autoLastStatus === 'error' && errorText(g.autoLastSummary?.error) ? `Error: ${errorText(g.autoLastSummary.error)}` : null,
  ].filter(Boolean).join('\n');
}

// ---- an event's badge (Dashboard) ----

// Worst first: one red group makes its event's badge red
const KIND_RANK = Object.freeze({ bad: 3, warn: 2, ok: 1, neutral: 0 });

// Runner statuses where nothing runs though the master switch is on: the
// badge's text and kind (the title takes runnerStatusInfo's words).
// paused_room is not one: it runs as soon as there's room.
const RUNNER_HALTED = Object.freeze({
  stopped: { kind: 'neutral', text: 'Auto · runner stopped' },
  no_session: { kind: 'bad', text: 'Auto · no login' },
  paused_auth: { kind: 'bad', text: 'Auto · login(s) down' },
});

const groupName = (g) => (g.name ? String(g.name) : `Group ${g.id}`);
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// What a group on Auto adds to its event's badge: its last run's kind, ok
// when it has not run since Auto was turned on (as autoChip)
function onGroupKind(g) {
  if (lastRunBeforeEnable(g) || !parseUtcDate(g.autoLastRunAt)) return 'ok';
  return lastStatusInfo(g.autoLastStatus).kind;
}

/**
 * The Dashboard's badge per event from GET /auto/status: Map<String(eventId),
 * { kind, text, title, groupCount, pausedCount }>, an event only when it has
 * a group on Auto (groupCount) or one the runner paused (pausedCount). The
 * runner's state comes first, so a green "Auto" never claims prices are being
 * written when they aren't:
 * - only paused groups: "Auto paused" (red)
 * - master switch off (enabled false, or status 'disabled'): "Auto · runner
 *   off" (grey)
 * - the runner can't run (status): "Auto · runner stopped" (stopped, grey),
 *   "Auto · no login" (no_session, red), "Auto · login dead" (paused_auth,
 *   red)
 * - dry run: "Auto · dry run" (grey)
 * - some on, some paused: "Auto · 1 paused" (amber)
 * - live (idle, working, paused_room): "Auto" ("Auto · 2 groups"), the worst
 *   last run among its groups (red > amber > green > grey; not run yet is
 *   green)
 * No next run in the title while nothing runs (switch off, runner halted).
 * enabled / dryRun not sent (top level, else config): unknown, the group
 * kinds decide. Empty for a missing or malformed status.
 */
export function autoBadgesByEvent(status) {
  const badges = new Map();
  if (!status || typeof status !== 'object' || !Array.isArray(status.groups)) return badges;
  const byEvent = new Map();
  for (const g of status.groups) {
    if (!g || typeof g !== 'object' || g.eventId === null || g.eventId === undefined || g.eventId === '') continue;
    const key = String(g.eventId);
    if (!byEvent.has(key)) byEvent.set(key, []);
    byEvent.get(key).push(g);
  }
  const switchedOff = (status.enabled ?? status.config?.enabled) === false || status.status === 'disabled';
  const halted = own(RUNNER_HALTED, status.status);
  const dryRun = !!(status.dryRun ?? status.config?.dryRun);

  for (const [key, groups] of byEvent) {
    const on = groups.filter(g => g.autoEnabled);
    const paused = groups.filter(g => !g.autoEnabled && g.autoLastStatus === 'paused');
    if (!on.length && !paused.length) continue;

    let kind;
    let text;
    let head;
    if (!on.length) {
      kind = 'bad';
      text = 'Auto paused';
      head = `Auto paused: the runner turned Auto off for ${plural(paused.length, 'group')} here, so nothing reprices `
        + `${paused.length === 1 ? 'it' : 'them'}. Fix the cause, then an admin turns Auto back on.`;
    } else if (switchedOff) {
      kind = 'neutral';
      text = 'Auto · runner off';
      head = `${plural(on.length, 'group')} set to Auto, but the runner's master switch is off: nothing runs and no price is written.`;
    } else if (halted) {
      kind = halted.kind;
      text = halted.text;
      head = `${plural(on.length, 'group')} set to Auto. ${runnerStatusInfo(status.status).text}`;
    } else if (dryRun) {
      kind = 'neutral';
      text = 'Auto · dry run';
      head = `${plural(on.length, 'group')} on Auto, in dry run: the runner computes each run and writes nothing to Skybox.`;
    } else if (paused.length) {
      kind = 'warn';
      text = `Auto · ${paused.length} paused`;
      head = `${plural(on.length, 'group')} on Auto (repriced unattended, written to Skybox); the runner paused ${paused.length}.`;
    } else {
      kind = 'neutral';
      for (const g of on) {
        const k = onGroupKind(g);
        if ((KIND_RANK[k] ?? 0) > KIND_RANK[kind]) kind = k;
      }
      text = on.length > 1 ? `Auto · ${on.length} groups` : 'Auto';
      head = `${plural(on.length, 'group')} on Auto: the server re-ladders ${on.length === 1 ? 'it' : 'them'} on a schedule `
        + 'and writes the prices to Skybox unattended.';
    }

    const lines = [
      head,
      ...on.map(g => `• ${groupName(g)}: on Auto, ${autoRunText(g)}`),
      ...paused.map(g => `• ${groupName(g)}: paused${g.pausedReason ? ` (${g.pausedReason})` : ''}`),
    ];
    if (on.length && !switchedOff && !halted) {
      const due = soonestDue(on);
      lines.push(due ? `Next run ${dueText(due)} (${easternText(due)}).` : 'No next run scheduled.');
    }
    lines.push('Click to open Lion Auto.');

    badges.set(key, { kind, text, title: lines.join('\n'), groupCount: on.length, pausedCount: paused.length });
  }
  return badges;
}

// ---- a run's summary ----

// selectWrites' skip reasons (backend autoGuards.js SKIP_REASONS) and the
// runner's own, in words; an unknown one is shown lower-cased
const SKIP_LABELS = Object.freeze({
  BAD_ID: 'bad listing id',
  DUPLICATE: 'listed twice',
  UNCHANGED: 'already at the ladder price',
  UNRESOLVED: 'no ladder price',
  BAD_STATUS: 'unexpected ladder answer',
  NO_CURRENT_PRICE: 'not priced yet',
  BAD_PRICE: 'bad price',
  NO_CHANGE: 'no price change',
  MAX_WRITES: 'over the per-run write limit',
  IN_OTHER_GROUP: 'also in another group',
  OVERLAP: 'also in another group',
  STALE: 'sold or repriced since read',
});

/** A skip reason in words */
export function skipLabel(reason) {
  return own(SKIP_LABELS, reason) || String(reason || 'other').toLowerCase().replace(/_/g, ' ');
}

const count = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
};

/** summary.skipped { REASON: n } → [{ reason, label, n }], largest first; total */
export function skippedBreakdown(skipped) {
  const rows = [];
  if (skipped && typeof skipped === 'object' && !Array.isArray(skipped)) {
    for (const [reason, v] of Object.entries(skipped)) {
      const n = count(v);
      if (n) rows.push({ reason, label: skipLabel(reason), n });
    }
  }
  rows.sort((a, b) => b.n - a.n || a.reason.localeCompare(b.reason));
  return { rows, total: rows.reduce((s, r) => s + r.n, 0) };
}

/**
 * The listings a dry run would have written: [{ inventoryId, from, to }]
 * from summary.wouldWrite (selectWrites' { inventoryId, newPrice,
 * expectedCurrentPrice }; currentPrice / currentB are read too)
 */
export function wouldWriteRows(summary) {
  const list = Array.isArray(summary?.wouldWrite) ? summary.wouldWrite : [];
  const num = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return list
    .filter(w => w && typeof w === 'object')
    .map(w => ({
      inventoryId: w.inventoryId ?? null,
      from: num(w.expectedCurrentPrice ?? w.currentPrice ?? w.currentB),
      to: num(w.newPrice ?? w.newB),
    }));
}

/** How many listings the run wrote, or (a dry run) would have written */
export function repricedCount(summary) {
  if (!summary || typeof summary !== 'object') return 0;
  const n = count(summary.repriced);
  return summary.dryRun ? Math.max(n, wouldWriteRows(summary).length) : n;
}

// What a summary says about the anchor comp: "zone anchor" when the zone
// fallback priced the group off its sections' zone (anchorScope 'zone', sent
// only then), and "1 low outlier dropped" when it sat above comps the
// low-outlier rule dropped (outliersDropped)
function anchorParts(summary) {
  const parts = [];
  if (summary.anchorScope === 'zone') parts.push('zone anchor');
  const outliers = outliersDroppedText(summary.outliersDropped);
  if (outliers) parts.push(outliers);
  return parts;
}

// An older summary's noComp (no applied, or no planned / written) is worded
// by the run status (autoLastStatus): these wrote their steps, an error one
// did not (that server kept the clock, the next run retried)
const WROTE_STATUSES = ['ok', 'partial'];
// summary.noComp.planned / written: a whole count >= 0, else null (an older summary)
const countOrNull = (v) => (Number.isInteger(v) && v >= 0 ? v : null);
// Wording only: a no-comp step is 6 h (lionAutoNoComp NO_COMP_STEP_HOURS,
// the server's NO_COMP_STEP_MS; lionAutoRunner.test.js pins them equal).
// Not imported: lionAutoNoComp imports this module.
const NO_COMP_EACH = '(6 h each)';

// What a run's no-comp step-down did (summary.noComp: the ladder's view of
// the clock, sent only when the step-down ran); null without one. steps 0:
// "no comp: no step due"; a dry run: "would step down N (6 h each)"; a
// paused run (Auto went off; turning it on restarts the clock): "step not
// written". Else by noComp.planned / written (the step-down writes the run
// selected and how many Skybox took; a HELD write never counts) and applied
// (the clock stored is the advanced one, stored right before the first
// write chunk): written > 0 "N step(s) down (6 h each)"; applied false
// (rolled back, or the run stopped before its first chunk) "N step(s) due,
// not written (retried next run)"; applied with planned 0 "N step(s) due,
// nothing to write (at the floor)"; applied with planned > 0 and written 0
// "N step(s) due, the writes were skipped or failed (the step is spent)".
// No applied, or no planned / written (an older summary): by the status.
function noCompPart(summary, status) {
  const nc = summary.noComp;
  if (!nc || typeof nc !== 'object' || Array.isArray(nc)) return null;
  const steps = Number(nc.steps);
  if (!(Number.isInteger(steps) && steps > 0)) return 'no comp: no step due';
  const stepsText = `${steps} step${steps === 1 ? '' : 's'}`;
  if (summary.dryRun || status === 'dry_run') return `no comp: would step down ${steps} ${NO_COMP_EACH}`;
  if (status === 'paused') return 'no comp: step not written';
  const planned = countOrNull(nc.planned);
  const written = countOrNull(nc.written);
  const counted = planned !== null && written !== null;
  if (counted && written > 0) return `no comp: ${stepsText} down ${NO_COMP_EACH}`;
  if (nc.applied === false) return `no comp: ${stepsText} due, not written (retried next run)`;
  if (nc.applied === true && counted) {
    return planned === 0
      ? `no comp: ${stepsText} due, nothing to write (at the floor)`
      : `no comp: ${stepsText} due, the writes were skipped or failed (the step is spent)`;
  }
  if (WROTE_STATUSES.includes(status)) return `no comp: stepped down ${steps}`;
  if (status === 'error') return 'no comp: step not written (retried next run)';
  return `no comp: ${stepsText} due`;
}

/**
 * A run's counts in a line: "3 repriced · 2 unchanged · 1 floored · skipped
 * 2 (1 over the per-run write limit, 1 also in another group)"; a dry run
 * says "would reprice 3". Holding (the ranks at the hold price: a run with
 * holding on) / capped / no price / failed show only when > 0, then "zone
 * anchor" when the zone fallback priced the group (summary.anchorScope) and
 * "1 low outlier dropped" when the anchor comp sat above comps the
 * low-outlier rule dropped (summary.outliersDropped), what the no-comp
 * step-down did (summary.noComp, worded by its planned / written and
 * applied, else by the run status: noCompPart), and
 * "stopped early" when the run will resume. An unchanged check: checkText.
 * "" for no summary.
 * @param {object|null} summary  the run's summary (autoLastSummary, a Run now's)
 * @param {string|null} [status] the run's status (autoLastStatus), null when unknown
 */
export function summaryText(summary, status = null) {
  if (!summary || typeof summary !== 'object') return '';
  if (summary.check === 'unchanged') return checkText(summary);
  const n = repricedCount(summary);
  const parts = [
    summary.dryRun ? `would reprice ${n}` : `${n} repriced`,
    `${count(summary.unchanged)} unchanged`,
    `${count(summary.floored)} floored`,
  ];
  if (count(summary.holding)) parts.push(`${count(summary.holding)} holding`);
  if (count(summary.capped)) parts.push(`${count(summary.capped)} capped`);
  if (count(summary.unresolved)) parts.push(`${count(summary.unresolved)} no price`);
  if (count(summary.failed)) parts.push(`${count(summary.failed)} failed`);
  parts.push(...anchorParts(summary));
  const noComp = noCompPart(summary, status);
  if (noComp) parts.push(noComp);
  const { rows, total } = skippedBreakdown(summary.skipped);
  if (total) parts.push(`skipped ${total} (${rows.map(r => `${r.n} ${r.label}`).join(', ')})`);
  if (summary.resume) parts.push('stopped early, the next run picks up');
  return parts.join(' · ');
}

/**
 * An unchanged check (check 'unchanged': the server skipped the ladder,
 * nothing moved since the last one) with the last full ladder's counts:
 * "checked: nothing moved since the last ladder (47m ago) · would reprice 2
 * · 3 unchanged · 1 no price · skipped 1 (1 no floor)". The age is the last
 * full ladder's (fpFullAt: the server never sends the fingerprint itself);
 * "would reprice" in a dry run only (a check writes nothing); floored,
 * holding, no price and skipped only when > 0; "zone anchor" and the low
 * outliers dropped as a run says them (the check's match sends both).
 */
function checkText(summary) {
  const at = parseUtcDate(summary.fpFullAt);
  const parts = [`checked: nothing moved since the last ladder${at ? ` (${agoText(at)})` : ''}`];
  if (summary.dryRun) parts.push(`would reprice ${repricedCount(summary)}`);
  parts.push(`${count(summary.unchanged)} unchanged`);
  if (count(summary.floored)) parts.push(`${count(summary.floored)} floored`);
  if (count(summary.holding)) parts.push(`${count(summary.holding)} holding`);
  if (count(summary.unresolved)) parts.push(`${count(summary.unresolved)} no price`);
  parts.push(...anchorParts(summary));
  const { rows, total } = skippedBreakdown(summary.skipped);
  if (total) parts.push(`skipped ${total} (${rows.map(r => `${r.n} ${r.label}`).join(', ')})`);
  return parts.join(' · ');
}

// ---- errors ----

/** A server-sent error (string, or { message } / { error }) as text; "" for none */
export function errorText(v) {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'object') return String(v.message || v.error || JSON.stringify(v));
  return String(v);
}

// The server's { error, code, details } of a failed call, as lines
function bodyOf(err) {
  return err?.response?.data && typeof err.response.data === 'object' ? err.response.data : {};
}

// "Run now"'s 409 codes (backend autoRunner RUN_ERROR_CODES), in words
const RUN_CONFLICTS = Object.freeze({
  AUTO_DISABLED: "The Auto runner's master switch is off: an admin turns it on in the Auto runner panel first.",
  AUTO_OFF: 'This group is not on Auto any more (someone turned it off, or the runner paused it).',
  AUTO_BUSY: 'Lion Auto is running another group, try again in a minute.',
  AUTO_NO_SESSION: 'Lion Auto has no working Skybox login (none assigned, or Skybox refused every one), so nothing ran. '
    + 'A super admin assigns one, or renews it/them, in Admin → Data.',
});

/** Whether a failed "Run now" failed for want of a working Auto login (link to Admin → Data) */
export function needsAutoLogin(err) {
  return err?.response?.status === 409 && bodyOf(err).code === 'AUTO_NO_SESSION';
}

/** A failed "Run now" in words */
export function describeRunError(err) {
  const status = err?.response?.status;
  const data = bodyOf(err);
  if (status === 409) {
    return own(RUN_CONFLICTS, data.code) || data.error || 'The run could not start now (another run may be in progress).';
  }
  if (status === 403) return data.code === 'ADMIN_ONLY' || !data.error ? 'Only an admin can run Auto.' : String(data.error);
  if (status === 404) {
    if (data.code === 'NOT_FOUND') return 'This group no longer exists (someone deleted it): reload the page.';
    return data.code ? (data.error || 'That group no longer exists.') : 'Auto runs are not available on this server yet.';
  }
  if (status === 429) return 'Too many requests: try again in a minute.';
  if (!status || err?.code === 'ECONNABORTED' || status === 502 || status === 503 || status === 504) {
    return 'Skybox or the server did not answer in time: the run may still finish. Check the group again in a minute.';
  }
  return data.error || 'The run failed.';
}

/** A failed config change in words */
export function describeConfigError(err) {
  const status = err?.response?.status;
  const data = bodyOf(err);
  if (status === 400) return data.error || 'The setting was not accepted.';
  if (status === 403) return 'Only an admin can change the Auto runner.';
  if (status === 404) return 'The Auto runner is not available on this server yet.';
  if (status === 429) return 'Too many requests: try again in a minute.';
  return data.error || err?.message || 'Saving the setting failed.';
}
